/**
 * A booking in progress, parked across an OAuth redirect.
 *
 * Google OAuth is a top-level navigation: the tab leaves for accounts.google.com
 * and comes back with the app remounted from scratch. Anything the client had
 * selected lives in React state and is gone. OTP never had this problem — the
 * code is typed on the same screen — which is why docs/ARCHITECTURE.md §3
 * originally specified OTP only.
 *
 * `sessionStorage` is the right store: per tab and per origin, so it survives the
 * round trip to Google, dies with the tab, and never leaks a half-finished
 * booking into the next visitor's session on a shared phone.
 *
 * **It does not survive iOS standalone PWA**, where the OAuth hop can be handed
 * to Safari and never returns to the installed app's tab. That is a known limit,
 * not a bug to chase — it is why OTP stays on screen as the fallback.
 */

const KEY = 'procalendar.pendingBooking';

/**
 * Deliberately minimal: the calendar module does not exist yet, so this holds
 * only what is already decided by the routing. Widen it when the booking flow
 * lands — and widen `isPendingBooking` in the same commit, or old payloads will
 * deserialize into a shape the flow no longer understands.
 */
export interface PendingBooking {
  /** The professional's public calendar: procalendar.app/:slug. */
  readonly slug: string;
  /** Selected slot starts, ISO 8601 UTC — the storage format is always UTC. */
  readonly slots: readonly string[];
  /** Epoch ms, used to expire stale intents. */
  readonly savedAt: number;
}

/**
 * A parked booking is only meaningful for as long as the slots are plausibly
 * still free. Past this, restoring it would show the client a selection that
 * another person may already have taken.
 */
const MAX_AGE_MS = 30 * 60 * 1000;

export function savePendingBooking(
  booking: Omit<PendingBooking, 'savedAt'>,
): void {
  try {
    const payload: PendingBooking = { ...booking, savedAt: Date.now() };
    sessionStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    // Private mode or a full quota. Losing the selection is survivable; the
    // client re-picks their slots. Blocking the login over it is not.
  }
}

/**
 * Reads and **clears** the parked booking. Consuming it once is the point: a
 * restore that left the payload behind would re-apply on every later mount.
 */
export function takePendingBooking(): PendingBooking | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!isPendingBooking(parsed)) return null;
  if (Date.now() - parsed.savedAt > MAX_AGE_MS) return null;
  return parsed;
}

export function clearPendingBooking(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing stored means nothing to clear.
  }
}

/**
 * The payload crosses a redirect and an app reload, so on the way back it is
 * untrusted input like any other — validate, never cast.
 */
function isPendingBooking(value: unknown): value is PendingBooking {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.slug === 'string' &&
    v.slug.length > 0 &&
    typeof v.savedAt === 'number' &&
    Number.isFinite(v.savedAt) &&
    Array.isArray(v.slots) &&
    v.slots.every((s) => typeof s === 'string')
  );
}
